import { useState, useCallback, useEffect } from 'react'
import { Position} from './types/Position'
import { LocationError} from './types/LocationError'
import { GeolocationOptions } from './types/GeolocationOptions'
import { PermissionState } from './types/PermissionState'
import { GeolocationService } from './services/GeolocationService'
import { PermissionService } from './services/PermissionService'

interface UseAdvancedLocationReturn {
    location: Position | null
    loading: boolean
    error: string | null
    permission: PermissionState
    permissionLoading: boolean
    isWatching: boolean
    watchId: number | null
    requestLocation: (options?: GeolocationOptions) => Promise<void>
    requestPermission: () => Promise<void>
    startWatching: (options?: GeolocationOptions) => Promise<void>
    stopWatching: () => void
    clearError: () => void
    clearLocation: () => void
}

export const useAdvancedLocation = (): UseAdvancedLocationReturn => {
    const [location, setLocation] = useState<Position | null>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [permissionLoading, setPermissionLoading] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)
    const [permission, setPermission] = useState<PermissionState>('prompt')
    const [watchId, setWatchId] = useState<number | null>(null)
    const isWatching = watchId !== null

    useEffect(() => {
        const checkInitialPermission = async () => {
            setPermissionLoading(true)

            try {
                const currentPermission = await PermissionService.checkGeolocationPermission()
                setPermission(currentPermission)
            } catch (err) {
                console.error('Permission check error:', err)
            } finally {
                setPermissionLoading(false)
            }
        }

        checkInitialPermission()
    }, [])

    const stopWatching = useCallback((): void => {
        if (watchId !== null) {
            GeolocationService.clearWatch(watchId)
            setWatchId(null)
        }
    }, [
        watchId
    ])

    useEffect(() => {
        const cleanup = PermissionService.onPermissionChange((newPermission) => {
            setPermission(newPermission)

            if (newPermission === 'denied' && watchId) {
                stopWatching()
            }
        })

        return cleanup
    }, [
        watchId,
        stopWatching
    ])

    const requestLocation = useCallback(async (options?: GeolocationOptions): Promise<void> => {
        if (permission === 'denied') {
            setError('Access to geolocation is denied. Please allow access in your browser settings')
            return
        }

        setLoading(true)
        setError(null)

        try {
            const position = await GeolocationService.getCurrentPosition(options)
            setLocation(position)
        } catch (err) {
            const locationError = err as LocationError
            setError(locationError.message)

            if (locationError.code === 1) {
                setPermission('denied')
            }
        } finally {
            setLoading(false)
        }
    }, [
        permission
    ])

    const requestPermission = useCallback(async (): Promise<void> => {
        setPermissionLoading(true)
        setError(null)

        try {
            const newPermission = await PermissionService.requestGeolocationPermission()
            setPermission(newPermission)

            if (newPermission === 'granted') {
                await requestLocation()
            }
        } catch (err) {
            setError('Error requesting permission for geolocation')
        } finally {
            setPermissionLoading(false)
        }
    }, [
        requestLocation
    ])

    const startWatching = useCallback(async (options?: GeolocationOptions): Promise<void> => {
        if (permission === 'denied') {
            setError('Unable to track location: access denied')

            return
        }

        if (isWatching) {
            stopWatching()
        }

        setError(null)

        try {
            const id = await GeolocationService.watchPosition(
                (newPosition) => {
                    setLocation(newPosition)
                },
                options
            )

            setWatchId(id)
        } catch (err) {
            const locationError = err as Error
            setError(`Error starting tracking: ${locationError.message}`)
        }
    }, [
        permission,
        isWatching,
        stopWatching
    ])

    const clearError = useCallback((): void => {
        setError(null)
    }, [])

    const clearLocation = useCallback((): void => {
        setLocation(null)
        setError(null)
    }, [])

    return {
        location,
        loading,
        error,
        permission,
        permissionLoading,
        isWatching,
        watchId,
        requestLocation,
        requestPermission,
        startWatching,
        stopWatching,
        clearError,
        clearLocation
    }
}